"use client";

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumb from '@/components/Breadcrumb';
import AppImage from '@/components/ui/AppImage';

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {if (e.isIntersecting) {e.target.classList.add('visible');obs.unobserve(e.target);}}),
      { threshold: 0.1 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

interface Project {
  id: string;
  title: string;
  client: string;
  category: string;
  year: string;
  metric: string;
  metricLabel: string;
  img: string;
  imgAlt: string;
  tags: string[];
  featured?: boolean;
}

const projects: Project[] = [
{
  id: 'meridian-rebrand',
  title: 'Meridian Rebrand',
  client: 'Meridian Brands',
  category: 'Brand Strategy',
  year: '2026',
  metric: '+38%',
  metricLabel: 'Revenue growth',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_132c14263-1772573027528.png",
  imgAlt: 'Meridian brand identity system displayed on various mockups including business cards, signage, and packaging',
  tags: ['Branding', 'Identity', 'Strategy'],
  featured: true
},
{
  id: 'pinnacle-platform',
  title: 'Pinnacle Platform',
  client: 'Pinnacle SaaS',
  category: 'Digital Transformation',
  year: '2025',
  metric: '4.5x',
  metricLabel: 'Lead volume',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_10e9254d2-1773313105303.png",
  imgAlt: 'Pinnacle SaaS platform dashboard showing analytics and user management screens on desktop and mobile',
  tags: ['SaaS', 'UX Design', 'Dev']
},
{
  id: 'solaris-campaign',
  title: 'Solaris Health Campaign',
  client: 'Solaris Health',
  category: 'Creative Production',
  year: '2025',
  metric: '12M+',
  metricLabel: 'Impressions',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_190170506-1773313104552.png",
  imgAlt: 'Health and wellness campaign photography showing people in active outdoor settings with brand overlays',
  tags: ['Campaign', 'Film', 'Photography']
},
{
  id: 'elektra-identity',
  title: 'Elektra Identity',
  client: 'Elektra Motors',
  category: 'Brand Strategy',
  year: '2025',
  metric: '-40%',
  metricLabel: 'Customer acquisition cost',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_12a41f818-1773313109931.png",
  imgAlt: 'Electric vehicle brand identity with modern typography and bold color palette applied to vehicle signage',
  tags: ['Automotive', 'Branding', 'Motion']
},
{
  id: 'onyx-ecommerce',
  title: 'Onyx & Co. Commerce',
  client: 'Onyx & Co.',
  category: 'Digital Transformation',
  year: '2024',
  metric: '$8.4M',
  metricLabel: 'Added revenue',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_11a235d36-1767000502864.png",
  imgAlt: 'Luxury e-commerce website redesign with product photography and streamlined checkout flow on multiple devices',
  tags: ['E-Commerce', 'UX', 'Strategy']
},
{
  id: 'vitalis-intake',
  title: 'Vitalis Intake System',
  client: 'Vitalis Systems',
  category: 'Growth Consulting',
  year: '2024',
  metric: '-65%',
  metricLabel: 'Admin time',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1dfe52c02-1772883271786.png",
  imgAlt: 'Healthcare intake system interface with patient management dashboard and appointment scheduling screens',
  tags: ['Healthcare', 'Automation', 'UX']
},
{
  id: 'prime-meridian-realty',
  title: 'Prime Meridian Realty',
  client: 'Prime Meridian',
  category: 'Brand Strategy',
  year: '2024',
  metric: '22%',
  metricLabel: 'Conversion rate',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1265036c4-1764676805673.png",
  imgAlt: 'Luxury real estate brand with elegant property photography, premium brochures, and digital listing pages',
  tags: ['Real Estate', 'Branding', 'Digital']
},
{
  id: 'nova-fintech',
  title: 'Nova Fintech Brand',
  client: 'Nova Financial',
  category: 'Brand Strategy',
  year: '2024',
  metric: '3.2x',
  metricLabel: 'Investor inquiries',
  img: "https://img.rocket.new/generatedImages/rocket_gen_img_1e9083752-1765224379343.png",
  imgAlt: 'Fintech startup brand identity with clean financial dashboard UI and investor presentation materials',
  tags: ['Fintech', 'Identity', 'Pitch Deck']
}];


const filters = ['All', 'Brand Strategy', 'Digital Transformation', 'Creative Production', 'Growth Consulting'];

export default function WorkPage() {
  useReveal();
  const sliderRef = useRef<HTMLDivElement>(null);
  const [activeFilter, setActiveFilter] = useState('All');
  const [scrollPct, setScrollPct] = useState(0);

  const filtered = activeFilter === 'All' ?
  projects :
  projects.filter((p) => p.category === activeFilter);

  useEffect(() => {
    const el = sliderRef.current;
    if (!el) return;
    const onScroll = () => {
      const max = el.scrollWidth - el.clientWidth;
      setScrollPct(max > 0 ? el.scrollLeft / max * 100 : 0);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [filtered]);

  const scrollSlider = (dir: 'left' | 'right') => {
    if (!sliderRef.current) return;
    const w = sliderRef.current.clientWidth * 0.8;
    sliderRef.current.scrollBy({ left: dir === 'right' ? w : -w, behavior: 'smooth' });
  };

  return (
    <>
      <Header />

      <main className="bg-background overflow-x-hidden">

        {/* ── MINIMAL HEADER ── */}
        <section
          className="pt-32 pb-14 px-6 md:px-12 border-b border-border"
          style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}>
          
          <div className="mb-8">
            <Breadcrumb crumbs={[{ label: 'Work' }]} />
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <span className="section-label mb-4 block">Selected Work</span>
              <h1
                className="fluid-h1 font-display font-light leading-none text-primary"
                style={{ fontFamily: 'var(--font-display)' }}>
                
                120+ projects.<br />Real results.
              </h1>
            </div>
            <p className="text-muted leading-relaxed max-w-sm">
              A selection of work across brand strategy, digital transformation, creative production, and growth consulting.
            </p>
          </div>
        </section>

        {/* ── FILTERS ── */}
        <section className="px-6 md:px-12 py-8 border-b border-border" style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}>
          <div className="flex flex-wrap gap-3">
            {filters.map((f) =>
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              className={`text-sm font-medium px-5 py-2 rounded-full border transition-all duration-200 ${
              activeFilter === f ?
              'bg-primary text-white border-primary' : 'bg-transparent text-muted border-border hover:border-primary hover:text-primary'}`
              }>
              
                {f}
              </button>
            )}
          </div>
        </section>

        {/* ── HORIZONTAL SCROLL PORTFOLIO ── */}
        <section className="py-16 relative">
          {/* Nav controls */}
          <div className="px-6 md:px-12 flex items-center justify-between mb-8" style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}>
            <span className="text-sm text-muted font-medium">{filtered.length} projects</span>
            <div className="flex gap-3">
              <button
                onClick={() => scrollSlider('left')}
                className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:border-primary hover:bg-primary hover:text-white transition-all duration-200 text-primary"
                aria-label="Scroll left">
                
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
              <button
                onClick={() => scrollSlider('right')}
                className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:border-primary hover:bg-primary hover:text-white transition-all duration-200 text-primary"
                aria-label="Scroll right">
                
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
            </div>
          </div>

          {/* Scroll container */}
          <div
            ref={sliderRef}
            className="snap-container hide-scrollbar flex gap-5 overflow-x-auto px-6 md:px-12 pb-4"
            style={{ scrollPaddingLeft: '24px' }}>
            
            {filtered.map((project, i) =>
            <div
              key={project.id}
              className={`snap-item flex-shrink-0 group cursor-pointer reveal reveal-delay-${i % 4 + 1}`}
              style={{ width: project.featured ? '520px' : '380px' }}>
              
                {/* Image */}
                <div
                className="relative overflow-hidden rounded-2xl mb-5"
                style={{ height: project.featured ? '380px' : '280px' }}>
                
                  <AppImage
                  src={project.img}
                  alt={project.imgAlt}
                  fill
                  className="object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                  sizes="(max-width: 768px) 380px, 520px" />
                
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  {/* Metric overlay */}
                  <div className="absolute bottom-5 left-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-accent text-white rounded-xl px-4 py-2">
                      <div
                      className="text-xl font-display font-semibold"
                      style={{ fontFamily: 'var(--font-display)' }}>
                      
                        {project.metric}
                      </div>
                      <div className="text-xs text-white/80">{project.metricLabel}</div>
                    </div>
                  </div>

                  {/* Featured badge */}
                  {project.featured &&
                <div className="absolute top-4 left-4 bg-accent text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                      Featured
                    </div>
                }
                </div>

                {/* Info */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {project.tags.map((tag) =>
                    <span
                      key={tag}
                      className="text-xs font-medium text-muted bg-card border border-border px-2.5 py-1 rounded-full">
                      
                          {tag}
                        </span>
                    )}
                    </div>
                    <h3
                    className="text-xl font-display font-light text-primary group-hover:text-accent transition-colors"
                    style={{ fontFamily: 'var(--font-display)' }}>
                    
                      {project.title}
                    </h3>
                    <div className="text-sm text-muted mt-1">{project.client} · {project.year}</div>
                  </div>
                  <div className="flex-shrink-0 w-8 h-8 rounded-full border border-border flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-all duration-200 mt-1">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6h8M7 3l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Progress bar */}
          <div className="px-6 md:px-12 mt-6" style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}>
            <div className="w-full h-0.5 bg-border rounded-full overflow-hidden">
              <div
                className="h-full bg-accent rounded-full transition-all duration-100"
                style={{ width: `${Math.max(8, scrollPct)}%` }} />
              
            </div>
          </div>
        </section>

        {/* ── CASE STUDY HIGHLIGHTS ── */}
        <section className="py-24 px-6 md:px-12" style={{ maxWidth: 'var(--container-max)', margin: '0 auto' }}>
          <div className="reveal mb-14">
            <span className="section-label mb-4 block">By the Numbers</span>
            <h2
              className="fluid-h2 font-display font-light text-primary"
              style={{ fontFamily: 'var(--font-display)' }}>
              
              What the work delivers
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {projects.slice(0, 4).map((project, i) =>
            <div
              key={project.id + '-highlight'}
              className={`reveal reveal-delay-${i % 2 + 1} flex gap-6 items-center p-8 rounded-2xl border border-border bg-card hover:border-accent/40 transition-colors duration-300 group`}>
              
                <div className="relative w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                  <AppImage
                  src={project.img}
                  alt={project.imgAlt}
                  fill
                  className="object-cover"
                  loading="lazy" />
                
                </div>
                <div className="flex-1">
                  <div className="text-xs text-muted font-medium uppercase tracking-wider mb-1">{project.category}</div>
                  <h3
                  className="text-lg font-display font-light text-primary mb-1 group-hover:text-accent transition-colors"
                  style={{ fontFamily: 'var(--font-display)' }}>
                  
                    {project.title}
                  </h3>
                  <div className="flex items-center gap-2">
                    <span
                    className="text-xl font-display font-semibold text-accent"
                    style={{ fontFamily: 'var(--font-display)' }}>
                    
                      {project.metric}
                    </span>
                    <span className="text-sm text-muted">{project.metricLabel}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ── BRIDGE CTA ── */}
        <section
          className="py-20 px-6 md:px-12 text-center"
          style={{ background: 'var(--color-secondary)' }}>
          
          <div className="reveal" style={{ maxWidth: '560px', margin: '0 auto' }}>
            <p className="text-muted text-lg mb-6 leading-relaxed">
              Want to see the full scope of what we do? Explore our services.
            </p>
            <Link href="/services" className="btn-outline">
              Explore Our Services
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M8 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </Link>
          </div>
        </section>

      </main>

      <Footer />
    </>);

}